import React, { useEffect, useState } from "react";
import { Route } from "react-router-dom";
import { Helmet } from "react-helmet";
import { logoutUser } from "@actions/logout";
import { getSellOrder, getSellOrders } from "@actions/sellOrderInfo";
import { getBuyOrders, getBuyOrder } from "@actions/buyOrders";
import { cancelSellOrder } from "@actions/sellActions";
import SelectOrderTypePanel from "@components/SelectOrderTypePanel";
import AddBankAccountModal from "@modules/UI/Popups/AddBankAccountModal";
import RemoveBankAccountModal from "@components/RemoveBankAccountModal";
import RemoveCreditCardModal from "@components/RemoveCreditCardModal";
import DeleteAccountModal from "@components/DeleteAccountModal";
import ChangeCustomerPassword from "@modules/UI/Popups/ChangeCustomerPassword";
import AddACreditCard from "@modules/UI/Popups/AddACreditCard";
import PaymentDetailsModal from "@modules/UI/Popups//PaymentDetailsModal";
import styled from "@emotion/styled";
import InnerContainerImageGradient from "@components/InnerContainerImageGradient";
import CustomerProfile from "@components/CustomerProfile";
import CreditCards from "@components/CreditCards";
import BankAccounts from "@components/BankAccounts";
import AccountHelpPopUp from "@modules/UI/Popups/AccountHelpPopUp";
import CustomerProfileEditModal from "@modules/UI/Popups/CustomerProfileEditModal";
import classNames from "classnames";
import { ClearedStyleRouterLink } from "@modules/Components";
import "../styles/account.scss";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import loadable from "@loadable/component";
import { initAppBrowser } from "@actions/initApp";
import MotionSensor from "@modules/Common/MotionSensor";
import LoginRequired from "../components/LoginRequired";

const CancelSellOrderModal = loadable(() =>
  import(
    /* webpackChunkName: "CancelSellOrderModal" */ "../components/CancelSellOrderModal"
  )
);
const CardCashSpinner = loadable(() =>
  import(
    /* webpackChunkName: "CardCashSpinner" */ "../components/CardCashSpinner"
  )
);

const Loader = styled.div`
  position: absolute;
  top: 200px;
  right: 0;
  left: 0;
  border-radius: 30px;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AccountMain = styled.div`
  background-color: #ffffff;
  padding-top: ${props => props.paddingTop};
  margin-bottom: 100px;
  min-height: 765px;

  @media (max-width: 975px) {
    padding-top: ${props => props.paddingTabletTop};
  }
  @media (max-width: 767px) {
    padding-top: ${props => props.paddingMobileTop};
  }
`;

const MainGrid = styled.div`
  grid-template-columns: 233px 1fr;
  display: grid;
  grid-gap: 100px;
  position: relative;

  @media (max-width: 1400px) {
    grid-template-columns: 200px 1fr;
    grid-gap: 80px;
  }

  @media (max-width: 975px) {
    display: block;
    width: 100% !important;
  }
  @media (max-width: 924px) {
    width: 85%;
  }
  @media (max-width: 728px) {
    width: 90%;
  }
`;

const Left = styled.div`
  position: sticky;
  top: 140px;
  height: 500px;

  @media (max-width: 975px) {
    height: 200px;
    box-shadow: 0 40px 40px 0 rgb(119 121 132 / 4%);
    background-color: #ffffff;
    height: 68px;
    z-index: 1;
    top: ${props => (props.scrolled ? "75px" : "108px")};
  }
`;

const Right = styled.div`
  padding-top: 41px;

  @media (max-width: 975px) {
    width: 95%;
    margin: 0 auto;
  }
  @media (max-width: 924px) {
    width: 100%;
    padding-top: 0px;
  }
`;

const Header = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #1f2021;
  margin-bottom: 20px;

  @media (max-width: 924px) {
    display: none;
  }
`;

const LargeScreen = styled.div`
  @media (max-width: 975px) {
    display: none;
  }
`;

const MobileScreen = styled.div`
  @media (min-width: 976px) {
    display: none;
  }
  height: 100%;
  width: 95%;
  margin: 0 auto;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: #1f2021;
  @media (max-width: 924px) {
    width: 85%;
  }
  @media (max-width: 728px) {
    width: 90%;
  }
`;

const DropdownHeader = styled.div`
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const DropdownHeaderName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #1f2021;
`;

const DropdownHeaderProfile = styled(DropdownHeaderName)`
  width: 95%;
  margin: 0 auto;
  padding-top: 27px;
  padding-bottom: 10px;
  @media (max-width: 924px) {
    width: 85%;
  }
  @media (max-width: 728px) {
    width: 90%;
  }
`;

const DropdownContainer = styled.div`
  background-color: #ffffff;
  position: absolute;
  width: 100%;
  left: 0;
  right: 0;
  top: 66px;
  padding-bottom: 20px;
`;

const DropDownInnerContainer = styled.div`
  height: auto;
  background-color: #ffffff;
  margin: 0 auto;
`;

const OrderTypeBox = styled.div`
  border-radius: 5px;
  box-shadow: 0 40px 40px 0 rgba(119, 121, 132, 0.16);
  background-color: #ffffff;
  margin-bottom: 60px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(119, 121, 132, 0.1);
`;

const Option = styled.div`
  color: rgba(31, 32, 33, 0.8);
  cursor: pointer;
  margin-bottom: 10px;
  width: 95%;
  margin: 0 auto;
  padding-top: 19px;
  padding-bottom: 19px;
  border-bottom: ${props =>
    props.itemId != 6 ? "1px solid rgba(119, 121, 132, 0.1)" : ""};

  @media (max-width: 924px) {
    width: 85%;
  }
  @media (max-width: 728px) {
    width: 90%;
  }
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Section = styled.div`
  box-shadow: 0 40px 40px 0 rgb(119 121 132 / 10%);
  background-color: #ffffff;
  margin-top: 3px;
`;

const Icon = styled.i`
  color: rgba(119, 121, 132, 0.5);
`;

const LinkSpan = styled.span`
  color: #1f2021;
  margin-bottom: 10px;
  display: block;
  width: 95%;
  margin: 0 auto;
  border-bottom: ${props =>
    props.itemId === 1 ? "1px solid rgba(119, 121, 132, 0.1)" : ""};
  padding-top: 19px;
  padding-bottom: 19px;

  @media (max-width: 924px) {
    width: 85%;
  }
  @media (max-width: 728px) {
    width: 90%;
  }
`;

const Link = styled(ClearedStyleRouterLink)`
  color: #1f2021;
`;

const SideBar = styled.div`
  position: absolute;
  left: 0;
  height: 32px;
  width: 3px;
  background-color: rgb(18, 178, 234);
  border-radius: 0px 100px 100px 0px;
`;

const RouteWithSubRoutes = route => {
  return (
    <Route
      path={route.path}
      render={props => (
        <route.component {...props} routes={route.routes} {...route} />
      )}
    />
  );
};

const actionRouteList = [
  {
    id: 1,
    displayName: "Gift Cards Purchased",
    link: "buy",
    route: "/buy"
  },
  {
    id: 2,
    displayName: "Gift Cards Sold",
    link: "sell",
    route: "/sell"
  },
  {
    id: 3,
    displayName: "Gift Cards Listed",
    link: "listed",
    route: "/listed"
  }
];

const actionList = [
  {
    id: 4,
    displayName: "My Profile",
    link: "profile"
  },
  {
    id: 5,
    displayName: "Credit Cards",
    link: "creditcards"
  },
  {
    id: 6,
    displayName: "Bank Accounts",
    link: "bankaccounts"
  },
  {
    id: 8,
    displayName: "Logout",
    link: "logout"
  }
];

const isScrolled = () => global.scrollY;

const Account = ({ route }) => {
  const location = useLocation();
  const path = location.pathname.split("/")[2] || "profile";

  const dispatch = useDispatch();
  const history = useHistory();
  const sellOrderToCancel = useSelector(
    state => state.ordersSell.sellOrderToCancel
  );
  const bannersList = useSelector(state => state.banners.bannersList);
  const showChangePasswordModal = useSelector(
    state => state.userActions.showChangePasswordModal
  );
  const popupsActions = useSelector(state => state.popupsActions);
  const showAddBankAccountModal = useSelector(
    state => state.ach.showAddBankAccountModal
  );
  const signedIn = useSelector(state => state.initAppReducer.signedIn);
  const customerGetStatus = useSelector(
    state => state.customerInfo.customerGetStatus
  );
  const customerConsignmentStatus = useSelector(
    state => state.customerInfo.riskConsignmentProposalStatus
  );

  const [display, setDisplay] = useState(path);
  const [orderLoading, setOrderLoading] = useState(false);
  const [openDropdown, changeOpenDropdown] = useState(false);
  const [logoutRunning, setLogoutRunning] = useState(false);
  const [setTheScroll, setScrolled] = useState(isScrolled());

  const setBuyOrders = async () => {
    const results = await dispatch(getBuyOrders());
    if (results?.length) {
      setOrderLoading(true);
      await dispatch(getBuyOrder(results[0].id));
      setOrderLoading(false);
    }
  };

  const setSellOrders = async () => {
    const results = await dispatch(getSellOrders());
    if (results?.length) {
      setOrderLoading(true);
      await dispatch(getSellOrder(results[0].id));
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    const sensor = new MotionSensor({ delay: 0 }).sense();

    sensor.watch(() => {
      setScrolled(isScrolled());
    });

    return () => {
      sensor.ignore();
    };
  }, []);

  useEffect(() => {
    setDisplay(path);
    if (path === "buy") {
      dispatch(initAppBrowser(["messages", "buy"]));
      setBuyOrders();
    } else if (path === "sell") {
      dispatch(initAppBrowser(["messages", "sell"]));
      setSellOrders();
    } else if (path === "listed") {
      dispatch(initAppBrowser(["messages"]));
    } else {
      dispatch(initAppBrowser(["messages"]));
    }
  }, [location.pathname, signedIn]);

  const getDisplayName = selected => {
    switch (selected) {
      case "buy":
        return "Gift Cards Purchased";
      case "sell":
        return "Gift Cards Sold";
      case "listed":
        return "Gift Cards Listed";
      case "profile":
        return "My Profile";
      case "creditcards":
        return "Credit Cards";
      case "bankaccounts":
        return "Bank Accounts";
      case "logout":
        return "Logout";
      default:
        return "Gift Cards Purchased";
    }
  };

  const panelIconClasses = classNames({
    "fa fa-angle-up fa-lg": openDropdown,
    "fa fa-angle-down fa-lg": !openDropdown
  });

  const logOutTheUser = async () => {
    if (logoutRunning) return;
    setLogoutRunning(true);
    const response = await dispatch(logoutUser());
    if (response.error) {
      setLogoutRunning(false);
    } else {
      history.push("/account/");
    }
  };

  const scrolled = setTheScroll > 125;

  return (
    <AccountMain
      paddingTop={bannersList.length > 0 ? "80px" : "180px"}
      paddingTabletTop={bannersList.length > 0 ? "50px" : "128px"}
      paddingMobileTop={bannersList.length > 0 ? "50px" : "108px"}
      onClick={() => {
        if (openDropdown) {
          changeOpenDropdown(false);
        }
      }}
    >
      <div>
        <InnerContainerImageGradient />
        <Helmet title={`My Account`} />
        {customerGetStatus === "initial" ? (
          <MainGrid className="main-container-page-width">
            <Loader>
              <CardCashSpinner />
            </Loader>
          </MainGrid>
        ) : customerGetStatus !== "initial" && !signedIn ? (
          <div style={{ display: "grid", textAlign: "center" }}>
            <LoginRequired />
          </div>
        ) : (
          <MainGrid className="main-container-page-width">
            <Left scrolled={scrolled}>
              <LargeScreen>
                <Header>Orders</Header>
                <OrderTypeBox>
                  <SelectOrderTypePanel
                    route="buy"
                    text="Gift Cards Purchased"
                    routerLink={true}
                    currentPage={location.pathname}
                    action={() => setDisplay("buy")}
                    display={display}
                  />
                  <Divider />
                  <SelectOrderTypePanel
                    route="sell"
                    text="Gift Cards Sold"
                    routerLink={true}
                    currentPage={location.pathname}
                    action={() => setDisplay("sell")}
                    display={display}
                  />
                  {!!customerConsignmentStatus && (
                    <>
                      <Divider />
                      <SelectOrderTypePanel
                        route="listed"
                        text="Gift Cards Listed"
                        routerLink={true}
                        currentPage={location.pathname}
                        action={() => setDisplay("listed")}
                        display={display}
                      />
                    </>
                  )}
                </OrderTypeBox>
                <Header>Account Settings</Header>
                <OrderTypeBox>
                  <SelectOrderTypePanel
                    text="My Profile"
                    route="profile"
                    action={() => setDisplay("profile")}
                    display={display}
                  />
                  <Divider />
                  <SelectOrderTypePanel
                    text="Credit Cards"
                    route="creditcards"
                    action={() => setDisplay("creditcards")}
                    display={display}
                  />
                  <Divider />
                  <SelectOrderTypePanel
                    text="Bank Accounts"
                    route="bankaccounts"
                    action={() => setDisplay("bankaccounts")}
                    display={display}
                  />
                  <Divider />
                  <SelectOrderTypePanel
                    text="Logout"
                    route="logout"
                    action={logOutTheUser}
                    display={display}
                  />
                </OrderTypeBox>
              </LargeScreen>
              <MobileScreen onClick={e => e.stopPropagation()}>
                <DropdownHeader
                  onClick={() => changeOpenDropdown(!openDropdown)}
                >
                  <DropdownHeaderName>
                    {openDropdown ? "Orders" : getDisplayName(display)}
                  </DropdownHeaderName>
                  <Icon className={panelIconClasses} />
                </DropdownHeader>
                {openDropdown && (
                  <DropdownContainer>
                    <DropDownInnerContainer>
                      <Section>
                        {actionRouteList
                          .filter(actions =>
                            !!customerConsignmentStatus
                              ? actions
                              : actions.id !== 3
                          )
                          .map((item, key) => (
                            <OptionContainer key={key}>
                              {display === item.link && <SideBar />}
                              <LinkSpan itemId={item.id}>
                                <Link
                                  to={`/account${item.route}/`}
                                  onClick={() => {
                                    setDisplay(item.link);
                                    changeOpenDropdown(false);
                                  }}
                                >
                                  {item.displayName}
                                </Link>
                              </LinkSpan>
                            </OptionContainer>
                          ))}
                      </Section>
                      <Section>
                        <DropdownHeaderProfile>
                          Account Settings
                        </DropdownHeaderProfile>
                        {actionList.map((item, key) => (
                          <OptionContainer key={key}>
                            {display === item.link && <SideBar />}
                            <Option
                              itemId={item.id}
                              onClick={() => {
                                setDisplay(item.link);
                                changeOpenDropdown(false);
                              }}
                            >
                              {item.displayName}
                            </Option>
                          </OptionContainer>
                        ))}
                      </Section>
                    </DropDownInnerContainer>
                  </DropdownContainer>
                )}
              </MobileScreen>
            </Left>

            <Right>
              {display === "buy" ||
              display === "sell" ||
              display === "listed" ? (
                <>
                  {route.routes.map((route, i) => (
                    <RouteWithSubRoutes
                      key={i}
                      {...route}
                      orderLoading={orderLoading}
                      setOrderLoading={setOrderLoading}
                    />
                  ))}
                </>
              ) : display === "profile" ? (
                <CustomerProfile />
              ) : display === "creditcards" ? (
                <CreditCards />
              ) : display === "bankaccounts" ? (
                <BankAccounts />
              ) : (
                <></>
              )}
            </Right>
          </MainGrid>
        )}
        {popupsActions.accountHelpBox && (
          <AccountHelpPopUp currentPage={display} />
        )}
        {popupsActions.showEditProfileModal && <CustomerProfileEditModal />}
        {showChangePasswordModal && <ChangeCustomerPassword />}
        {popupsActions.addACreditCard && <AddACreditCard />}
        {showAddBankAccountModal && <AddBankAccountModal />}
        {popupsActions.showPaymentDetailsModal && <PaymentDetailsModal />}
        <CancelSellOrderModal
          cancelSellOrder={() => dispatch(cancelSellOrder(sellOrderToCancel))}
        />
        <RemoveBankAccountModal />
        <RemoveCreditCardModal />
        {popupsActions.showDeleteAccountModal && <DeleteAccountModal />}
      </div>
    </AccountMain>
  );
};

export default Account;
